import { pdfjs } from 'react-pdf';
import { ContextDocumentUploadError } from './context-document-upload-error';
import { normalizeAttachment } from '../../supabase/functions/_shared/osAttachmentNormalization';
import { documentAccept, recognizeDocument, usesServerNormalizer, type DocumentKind } from '../../supabase/functions/_shared/documentFormats';

export {
  ContextDocumentUploadError,
  getContextDocumentUploadErrorMessage,
} from './context-document-upload-error';

export const CONTEXT_DOCUMENT_BUCKET = 'conversation-documents';
export const MAX_CONTEXT_DOCUMENT_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_CONTEXT_DOCUMENT_TEXT_CHARS = 200_000;
export const MAX_ACTIVE_CONTEXT_DOCUMENTS = 12;
export const CONTEXT_DOCUMENT_QUOTA_MESSAGE =
  'You can use up to 12 source files in this conversation. Remove one before adding another.';

type QuotaDocument = { id: string; active: boolean; status: string };
export type NamedContextDocument = QuotaDocument & {
  file_name: string;
  content_sha256?: string | null;
};

export type ContextDocumentDecision<T extends NamedContextDocument> =
  | { kind: 'duplicate' | 'cancelled' | 'quota' | 'new'; existing?: never }
  | { kind: 'replace'; existing: T };

export function normalizeContextDocumentName(fileName: string): string {
  return fileName.trim().toLowerCase();
}

export async function sha256ContextDocumentBytes(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

export async function resolveContextDocumentUpload<T extends NamedContextDocument>(
  documents: readonly T[],
  fileName: string,
  contentSha256: string,
  confirmReplacement: (existing: T) => boolean | Promise<boolean>,
): Promise<ContextDocumentDecision<T>> {
  const existing = documents.find(document =>
    normalizeContextDocumentName(document.file_name) === normalizeContextDocumentName(fileName)
  );
  if (existing) {
    if (existing.content_sha256 === contentSha256) return { kind: 'duplicate' };
    return await confirmReplacement(existing)
      ? { kind: 'replace', existing }
      : { kind: 'cancelled' };
  }
  return countReservedContextDocumentSlots(documents) < MAX_ACTIVE_CONTEXT_DOCUMENTS
    ? { kind: 'new' }
    : { kind: 'quota' };
}

export function countReservedContextDocumentSlots(documents: readonly QuotaDocument[]): number {
  return documents.filter(document =>
    document.status === 'uploading' || (document.status === 'ready' && document.active)
  ).length;
}

export function selectContextDocumentUploads<T>(
  documents: readonly QuotaDocument[],
  candidates: readonly T[],
  getReplacedDocumentId?: (candidate: T) => string | null | undefined,
): { accepted: T[]; rejected: T[] } {
  let remaining = Math.max(0, MAX_ACTIVE_CONTEXT_DOCUMENTS - countReservedContextDocumentSlots(documents));
  const replacedIds = new Set<string>();
  const accepted: T[] = [];
  const rejected: T[] = [];

  for (const candidate of candidates) {
    const replacementId = getReplacedDocumentId?.(candidate);
    const replacesReservedSlot = !!replacementId &&
      !replacedIds.has(replacementId) &&
      documents.some(document => document.id === replacementId &&
        (document.status === 'uploading' || (document.status === 'ready' && document.active)));
    if (replacesReservedSlot || remaining > 0) {
      accepted.push(candidate);
      if (replacesReservedSlot) replacedIds.add(replacementId!);
      else remaining -= 1;
    } else {
      rejected.push(candidate);
    }
  }

  return { accepted, rejected };
}

pdfjs.GlobalWorkerOptions.workerSrc =
  pdfjs.GlobalWorkerOptions.workerSrc || `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const CONTEXT_DOCUMENT_ACCEPT = documentAccept();

export function getSupportedContextDocumentKind(file: File): DocumentKind | null {
  return recognizeDocument(file.name, file.type || '');
}

export function isServerNormalizedContextDocument(file: File): boolean {
  const kind = getSupportedContextDocumentKind(file);
  return !!kind && usesServerNormalizer(kind);
}

export async function processContextDocumentFiles<T>(
  files: readonly T[],
  upload: (file: T) => Promise<void>,
  onError: (file: T, error: unknown) => void,
): Promise<void> {
  for (const file of files) {
    try {
      await upload(file);
    } catch (error) {
      onError(file, error);
    }
  }
}

export function replaceContextDocument<T extends { id: string }>(
  documents: readonly T[],
  document: T,
): T[] {
  const index = documents.findIndex(item => item.id === document.id);
  if (index === -1) return [...documents, document];
  return documents.map((item, itemIndex) => itemIndex === index ? document : item);
}

export function mergeContextDocumentLists<T extends { id: string }>(
  loaded: readonly T[],
  recentlyUploaded: readonly T[],
): T[] {
  const loadedIds = new Set(loaded.map(document => document.id));
  return [...loaded, ...recentlyUploaded.filter(document => !loadedIds.has(document.id))];
}

export function sanitizeContextDocumentFileName(fileName: string): string {
  const trimmed = fileName.trim().replace(/[/\\]+/g, '-');
  const normalized = trimmed
    .normalize('NFKD')
    .replace(/[^\w.\- ]+/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^\.+/, '')
    .slice(0, 120);
  return normalized || 'document';
}

function normalizeExtractedText(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/[ \t\f\v]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

async function extractPdfText(file: File): Promise<string> {
  const bytes = await file.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data: bytes });
  const pdf = await loadingTask.promise;
  const pages: string[] = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const textContent = await page.getTextContent();
    const text = textContent.items
      .map((item) => ('str' in item && typeof item.str === 'string' ? item.str : ''))
      .filter(Boolean)
      .join(' ');
    if (text.trim()) pages.push(text);

    const currentLength = pages.reduce((sum, pageText) => sum + pageText.length, 0);
    if (currentLength > MAX_CONTEXT_DOCUMENT_TEXT_CHARS) {
      throw new ContextDocumentUploadError('Extracted text is too large. Use a shorter document.');
    }
  }

  return normalizeExtractedText(pages.join('\n\n'));
}

export async function extractContextDocumentText(file: File): Promise<string> {
  if (file.size > MAX_CONTEXT_DOCUMENT_FILE_BYTES) {
    throw new ContextDocumentUploadError('File is too large. Upload a file up to 10 MB.');
  }

  const kind = getSupportedContextDocumentKind(file);
  if (!kind) {
    throw new ContextDocumentUploadError('Unsupported source file type.');
  }
  if (usesServerNormalizer(kind)) throw new ContextDocumentUploadError('This file must be extracted after upload.');

  const rawText = kind === 'pdf'
    ? await extractPdfText(file)
    : kind === 'docx' || kind === 'xlsx'
      ? await normalizeAttachment(new Uint8Array(await file.arrayBuffer()), kind) || ''
      : await file.text();
  const text = normalizeExtractedText(rawText);

  if (!text) {
    throw new ContextDocumentUploadError(
      kind === 'pdf'
        ? 'No readable text was found. Scanned PDFs are not supported yet.'
        : 'No readable text was found in this file.',
    );
  }

  if (text.length > MAX_CONTEXT_DOCUMENT_TEXT_CHARS) {
    throw new ContextDocumentUploadError('Extracted text is too large. Use a shorter document.');
  }

  return text;
}
